/**
 * Flatten included report structure into the same hierarchy the backend PDF uses:
 * templates (tree / saved order) → sections (catalog / position order) → fields (catalog / position order).
 * Backend does NOT print template titles — only section names.
 */

import { fieldAllowsPhotos, getCatalogFieldNames, getCatalogSectionNames, sortByCatalogOrder } from "./catalogOrder";

const SAMPLE_VALUES = ["Example Test Text 1", "Example Test Text 2", "Example Test Text 3", "Example Test Text 4", "Complete", "Yes", "N/A", "$1,250", "12"];

const looksLikeCurrency = (fieldName = "") => /cost|price|amount|fee|currency|\$/i.test(String(fieldName));

const looksLikeNumber = (fieldName = "") => /qty|quantity|count|number|#|height|width|length/i.test(String(fieldName));

const hashKey = (key) => {
	let hash = 0;
	for (let i = 0; i < key.length; i++) {
		hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
	}
	return hash;
};

/** Stable placeholder per field so adding/removing other fields does not reshuffle values. */
const sampleForField = (templateName, sectionName, fieldName) => {
	const hash = hashKey(`${templateName}\0${sectionName}\0${fieldName}`);
	const raw = SAMPLE_VALUES[hash % SAMPLE_VALUES.length];
	if (looksLikeCurrency(fieldName)) {
		return raw.startsWith("$") ? raw : `$${raw.replace(/[^\d,]/g, "") || "1,250"}`;
	}
	if (looksLikeNumber(fieldName)) return "12";
	return raw;
};

export const buildPreviewModel = ({ reportName, saveName, coverImgName, templateData, catalog }) => {
	const included = templateData || {};
	const sections = [];

	Object.keys(included)
		.filter((name) => included[name] && included[name].deleted !== true)
		.forEach((templateName) => {
			const entry = included[templateName];
			const catalogSectionOrder = getCatalogSectionNames(catalog, templateName);

			const activeSections = (entry.includedSections || []).filter((s) => s && s.deleted !== true);
			const orderedSections = sortByCatalogOrder(
				activeSections.map((s) => s.name),
				catalogSectionOrder,
			).map((name) => activeSections.find((s) => s.name === name));

			orderedSections.forEach((section) => {
				if (!section) return;
				const catalogFieldOrder = getCatalogFieldNames(catalog, templateName, section.name);
				const activeFields = (entry.includedFields?.[section.name] || []).filter((f) => f && f.deleted !== true);
				const orderedFieldNames = sortByCatalogOrder(
					activeFields.map((f) => f.name),
					catalogFieldOrder,
				);

				const fields = orderedFieldNames.map((fieldName) => {
					const field = activeFields.find((f) => f.name === fieldName);
					const hash = hashKey(`${templateName}\0${section.name}\0${fieldName}`);

					return {
						id: field?.id,
						name: fieldName,
						sampleValue: sampleForField(templateName, section.name, fieldName),
						showPhotos: fieldAllowsPhotos(catalog, templateName, section.name, fieldName),
						showComment: hash % 5 === 0,
						comment: "Field notes from site visit.",
					};
				});

				if (fields.length === 0) return;

				sections.push({
					id: section.id,
					name: section.name,
					templateName,
					fields,
				});
			});
		});

	const now = new Date();
	const month = now.getMonth() + 1;
	const dateString = `Report Created: ${month.toString().padStart(2, "0")}/${now.getDate().toString().padStart(2, "0")}/${now.getFullYear()}`;

	return {
		reportName: reportName || "Untitled report",
		saveName: saveName || "",
		coverImgName: coverImgName || "",
		sections,
		dateString,
	};
};
